import { computed, watch, type WritableComputedRef } from 'vue';
import { toast } from '@beonit/adaptive-raindrop';
import { feedbackFor } from '../feedback.js';
import { metricsReading } from '../lib/metrics-reading.js';
import { usePreferences, useUpdatePreferences } from '../queries.js';

/**
 * «Métricas globales» (product owner, 2026-09-27): every figure over the whole dataset, on every
 * screen of the platform, kept per user on the server — it follows the person from one browser to
 * the next.
 *
 * Reading it anywhere keeps the shared reading (`metricsReading`) in step with the server, so every
 * figure read through `productFigure` follows; setting it changes every screen at once and saves it.
 * The module's navigation reads it on every screen, so the choice is in force before any figure is
 * painted.
 */
export function useGlobalMetrics(): WritableComputedRef<boolean> {
  const { data } = usePreferences();
  const update = useUpdatePreferences();

  watch(
    () => data.value?.globalMetrics,
    (value) => {
      if (value !== undefined) metricsReading.global = value;
    },
    { immediate: true },
  );

  return computed({
    get: () => data.value?.globalMetrics ?? metricsReading.global,
    set: (value: boolean) => {
      metricsReading.global = value;
      update.mutate(
        { globalMetrics: value },
        { onError: (error) => toast.error(feedbackFor(error)) },
      );
    },
  });
}
